export const simulateTraditionalRental = (params) => {
  const {
    propertyValue,
    propertyGrowthRate,
    rentGrowthRate,
    taxRate,
    insuranceRate,
    maintenancePercentOfRent,
    vacancyRate,
    adminFeePercent,
    termYears,
    sellCostRate,
    monthlyRentRatio,
  } = params;

  const yearlyData = [];
  let currentAssetValue = propertyValue;
  let currentRent = propertyValue / monthlyRentRatio;

  let cumulativeGrossRent = 0;
  let cumulativeRevenue = 0;
  let cumulativeExpenses = 0;
  let cumulativeNetCashflow = 0;

  let totalVacancyLoss = 0;
  let totalAdminFees = 0;

  let firstYearRent = currentRent;
  let lastYearRent = 0;

  for (let year = 1; year <= termYears; year++) {
    const grossRent = currentRent * 12;
    cumulativeGrossRent += grossRent;
    cumulativeRevenue += grossRent;

    const vacancyLoss = grossRent * (vacancyRate / 100);
    totalVacancyLoss += vacancyLoss;
    const netRentCollected = grossRent - vacancyLoss;

    const taxPaid = currentAssetValue * (taxRate / 100);
    const insurancePaid = currentAssetValue * (insuranceRate / 100);
    const maintenancePaid = grossRent * (maintenancePercentOfRent / 100);
    const adminFeePaid = grossRent * (adminFeePercent / 100);
    totalAdminFees += adminFeePaid;

    const totalExpenses = taxPaid + insurancePaid + maintenancePaid + adminFeePaid;
    cumulativeExpenses += totalExpenses;

    const netCashflowYearly = netRentCollected - totalExpenses;
    cumulativeNetCashflow += netCashflowYearly;

    const estimatedSellCost = currentAssetValue * (sellCostRate / 100);
    const projectedExitValueThisYear =
      cumulativeNetCashflow + currentAssetValue - estimatedSellCost;

    yearlyData.push({
      year,
      assetValue: Math.round(currentAssetValue),
      rentCollected: Math.round(grossRent),
      vacancyLoss: Math.round(vacancyLoss),
      netRentCollected: Math.round(netRentCollected),
      taxPaid: Math.round(taxPaid),
      insurancePaid: Math.round(insurancePaid),
      maintenancePaid: Math.round(maintenancePaid),
      adminFeePaid: Math.round(adminFeePaid),
      totalInvestorExpenses: Math.round(totalExpenses),
      netCashflowYearly: Math.round(netCashflowYearly),
      cumulativeGrossRent: Math.round(cumulativeGrossRent),
      cumulativeRevenue: Math.round(cumulativeRevenue),
      cumulativeExpenses: Math.round(cumulativeExpenses),
      cumulativeNetCashflow: Math.round(cumulativeNetCashflow),
      exitValueLine: Math.round(projectedExitValueThisYear),

      cumulativeTax: Math.round(yearlyData.reduce((sum, y) => sum + (y.taxPaid || 0), 0) + taxPaid),
      cumulativeInsurance: Math.round(yearlyData.reduce((sum, y) => sum + (y.insurancePaid || 0), 0) + insurancePaid),
      cumulativeMaintenance: Math.round(yearlyData.reduce((sum, y) => sum + (y.maintenancePaid || 0), 0) + maintenancePaid),
      cumulativeAdminFees: Math.round(yearlyData.reduce((sum, y) => sum + (y.adminFeePaid || 0), 0) + adminFeePaid),
      cumulativeVacancyLoss: Math.round(yearlyData.reduce((sum, y) => sum + (y.vacancyLoss || 0), 0) + vacancyLoss),
      cumulativeNetIncome: Math.round(cumulativeNetCashflow),
    });

    currentAssetValue *= 1 + propertyGrowthRate / 100;
    currentRent = (currentAssetValue / monthlyRentRatio) * (1 + rentGrowthRate / 100);
    lastYearRent = currentRent;
  }

  // Final Calculations
  const finalYear = yearlyData[yearlyData.length - 1];
  const finalAssetValue = finalYear.assetValue;
  const sellCost = finalAssetValue * (sellCostRate / 100);
  const netAssetValueAfterSale = finalAssetValue - sellCost;
  const trueExitValue = netAssetValueAfterSale + cumulativeNetCashflow;

  const initialInvestment = propertyValue;
  const grossProfit = trueExitValue - initialInvestment;
  const totalROI = (grossProfit / initialInvestment) * 100;
  const cashOnCashReturn = (cumulativeNetCashflow / initialInvestment) * 100;
  const annualizedROI = (Math.pow(1 + totalROI / 100, 1 / termYears) - 1) * 100;
  const irrEstimate = annualizedROI;

  const totalOperatingExpenses = yearlyData.reduce(
    (sum, y) => sum + y.taxPaid + y.insurancePaid + y.maintenancePaid,
    0
  );

  const noi = cumulativeGrossRent - totalOperatingExpenses;
  const annualNOI = noi / termYears;
  const capRate = (annualNOI / propertyValue) * 100;

  const investorResult = {
    model: 'Traditional Rental',
    originalPropertyValue: Math.round(propertyValue),
    initialInvestment: Math.round(initialInvestment),
    termYears,
    finalValue: Math.round(finalAssetValue),
    totalRentCollected: Math.round(cumulativeRevenue),
    netCashflow: Math.round(cumulativeNetCashflow),
    cumulativeGrossRent: Math.round(cumulativeGrossRent),
    cumulativeExpenses: Math.round(cumulativeExpenses),
    cumulativeNetCashflow: Math.round(cumulativeNetCashflow),
    assetAppreciation: Math.round(finalAssetValue - propertyValue),
    firstYearRent: Math.round(firstYearRent * 12),
    lastYearRent: Math.round(lastYearRent * 12),

    // Key additions
    totalAdminFees: Math.round(totalAdminFees),
    totalVacancyLoss: Math.round(totalVacancyLoss),
    sellCost: Math.round(sellCost),
    netAssetValueAfterSale: Math.round(netAssetValueAfterSale),
    exitValue: Math.round(trueExitValue),
    finalNetProfit: Math.round(trueExitValue - initialInvestment),

    // Return & performance metrics
    totalROI: totalROI.toFixed(1),
    annualizedROI: annualizedROI.toFixed(1),
    cashOnCashReturn: cashOnCashReturn.toFixed(1),
    irrEstimate: irrEstimate.toFixed(1),
    annualNOI: Math.round(annualNOI),
    capRate: parseFloat(capRate.toFixed(2)),
  };

  const borrowerResult = {
    model: 'Traditional Rental',
    firstYearRent: Math.round(firstYearRent * 12),
    lastYearRent: Math.round(lastYearRent * 12),
    totalBorrowerPaid: Math.round(cumulativeGrossRent),
    equityBuilt: 0,
  };

  console.log('Traditional Rent',yearlyData);
  return {
    investorResult,
    borrowerResult,
    yearlyData,
  };
};
